import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const BASE_MS_PER_WORD = 280;

const LCVTutorDialog = ({
  text,
  label,
  animated = false,
  onFinished,
  speed: externalSpeed,
  fontSize: externalFontSize,
  showControls = true,
  paused = false,
  onSpeedChange,
  onFontSizeChange,
  onRestart,
  onProgress,
}) => {
  const [internalSpeed, setInternalSpeed] = useState(1);
  const [internalFontSize, setInternalFontSize] = useState(17);
  const [wordIndex, setWordIndex] = useState(0);

  const speed = externalSpeed ?? internalSpeed;
  const fontSize = externalFontSize ?? internalFontSize;
  const handleSpeedChange = onSpeedChange ?? setInternalSpeed;
  const handleFontSizeChange = onFontSizeChange ?? setInternalFontSize;

  const words = useMemo(
    () => String(text || '').trim().split(/\s+/).filter(Boolean),
    [text],
  );
  const finishedRef = useRef(false);
  const containerRef = useRef(null);
  const activeWordRef = useRef(null);

  useEffect(() => {
    setWordIndex(0);
    finishedRef.current = false;
  }, [text]);

  useEffect(() => {
    onProgress?.({
      spokenWords: Math.min(wordIndex, words.length),
      totalWords: words.length,
    });
  }, [onProgress, wordIndex, words.length]);

  useEffect(() => {
    if (!animated || paused || words.length === 0) return undefined;
    if (wordIndex >= words.length) {
      if (!finishedRef.current) {
        finishedRef.current = true;
        onFinished?.();
      }
      return undefined;
    }

    const timer = window.setTimeout(
      () => setWordIndex(current => current + 1),
      BASE_MS_PER_WORD / speed,
    );

    return () => window.clearTimeout(timer);
  }, [animated, onFinished, paused, speed, wordIndex, words.length]);

  useEffect(() => {
    if (!activeWordRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const activeWord = activeWordRef.current;
    const wordBottom = activeWord.offsetTop + activeWord.offsetHeight;
    const visibleBottom = container.scrollTop + container.clientHeight;

    if (wordBottom > visibleBottom - 4) {
      container.scrollTop = wordBottom - container.clientHeight + 8;
    }
  }, [wordIndex]);

  const handleRestart = useCallback(() => {
    if (onRestart) {
      onRestart();
      return;
    }

    setWordIndex(0);
    finishedRef.current = false;
  }, [onRestart]);

  const speedPct = Math.round((speed - 1) * 100);
  const speedLabel =
    speedPct === 0 ? '1x' : speedPct > 0 ? `+${speedPct}%` : `${speedPct}%`;

  return (
    <div className="lcv-tutor-dialog">
      <div className="lcv-tutor-dialog-header">
        <div className="lcv-tutor-avatar-img" aria-hidden="true">
          🎩
        </div>
        <div className="lcv-tutor-name">Benjamin Franklin</div>
        {label && <span className="lcv-tutor-phase-label">{label}</span>}
      </div>

      <div
        ref={containerRef}
        className="lcv-tutor-dialog-text"
        style={{fontSize: `${fontSize}px`}}
      >
        {animated
          ? words.map((word, index) => (
              <span
                key={`${word}-${index}`}
                ref={index === wordIndex - 1 ? activeWordRef : undefined}
                className={
                  index < wordIndex ? 'lcv-tw-spoken' : 'lcv-tw-pending'
                }
              >
                {word}{' '}
              </span>
            ))
          : text}
      </div>

      {showControls && (
        <div className="lcv-tutor-toolbar">
          <div className="lcv-tutor-tool-group">
            <span className="lcv-tutor-tool-label">Speed</span>
            <button
              type="button"
              className="lcv-tutor-tool-btn"
              disabled={speed <= 0.7}
              onClick={() =>
                handleSpeedChange(Math.max(0.7, +(speed - 0.1).toFixed(1)))
              }
            >
              -
            </button>
            <span className="lcv-tutor-tool-value">{speedLabel}</span>
            <button
              type="button"
              className="lcv-tutor-tool-btn"
              disabled={speed >= 1.3}
              onClick={() =>
                handleSpeedChange(Math.min(1.3, +(speed + 0.1).toFixed(1)))
              }
            >
              +
            </button>
          </div>

          <div className="lcv-tutor-tool-group">
            <span className="lcv-tutor-tool-label">Text</span>
            <button
              type="button"
              className="lcv-tutor-tool-btn"
              disabled={fontSize <= 14}
              onClick={() => handleFontSizeChange(fontSize - 1)}
            >
              A-
            </button>
            <span className="lcv-tutor-tool-value">{fontSize}px</span>
            <button
              type="button"
              className="lcv-tutor-tool-btn"
              disabled={fontSize >= 24}
              onClick={() => handleFontSizeChange(fontSize + 1)}
            >
              A+
            </button>
          </div>

          {animated && (
            <button
              type="button"
              className="lcv-tutor-tool-restart"
              onClick={handleRestart}
            >
              ↻ Watch Again
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LCVTutorDialog;
